import React, { useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { COLORS } from '../theme/theme';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Por favor, completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const success = await login(username, password);
      if (success) {
        navigate('/');
      } else {
        setError('Usuario o contraseña incorrectos');
        setPassword('');
        setLoading(false);
      }
    } catch {
      setError('Error de conexión. Inténtalo de nuevo.');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
      }}
    >
      {/* Panel izquierdo - Formulario */}
      <Box
        sx={{
          flex: '0 0 50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          px: 8,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* Logo */}
          <Box sx={{ mb: 5 }}>
            <img
              src="/logo-met.png"
              alt="MET Energía"
              style={{ height: 44 }}
            />
          </Box>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: COLORS.navy,
              fontWeight: 800,
              mb: 0.5,
            }}
          >
            Iniciar sesión
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 4 }}>
            Accede a MET Licitator para analizar licitaciones
          </Typography>

          <form onSubmit={handleSubmit} noValidate>
            <Typography
              component="label"
              htmlFor="met-username"
              variant="body2"
              sx={{ color: COLORS.navy, mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}
            >
              Email
            </Typography>
            <TextField
              id="met-username"
              fullWidth
              placeholder="Tu dirección de email"
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="email"
              autoFocus
              size="small"
              inputProps={{ 'aria-describedby': error ? 'met-error' : undefined }}
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  '& fieldset': { borderColor: '#d0d5dd' },
                  '&:hover fieldset': { borderColor: COLORS.teal },
                  '&.Mui-focused fieldset': { borderColor: COLORS.teal },
                },
              }}
            />

            <Typography
              component="label"
              htmlFor="met-password"
              variant="body2"
              sx={{ color: COLORS.navy, mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}
            >
              Contraseña
            </Typography>
            <TextField
              id="met-password"
              fullWidth
              placeholder="Tu contraseña"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              size="small"
              inputProps={{ 'aria-describedby': error ? 'met-error' : undefined }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      size="small"
                      aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  '& fieldset': { borderColor: '#d0d5dd' },
                  '&:hover fieldset': { borderColor: COLORS.teal },
                  '&.Mui-focused fieldset': { borderColor: COLORS.teal },
                },
              }}
            />

            {error && (
              <Alert id="met-error" severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mb: 2,
                backgroundColor: COLORS.teal,
                textTransform: 'uppercase',
                fontWeight: 700,
                fontSize: '0.9rem',
                letterSpacing: '0.1em',
                py: 1.3,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: COLORS.tealDark,
                },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Iniciar Sesión'}
            </Button>
          </form>

          {process.env.NODE_ENV === 'development' && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 1.5 }}>
              <Typography variant="caption">
                <strong>Desarrollo:</strong> Usuario y contraseña se configuran en el archivo .env
              </Typography>
            </Alert>
          )}
        </Box>
      </Box>

      {/* Panel derecho - Branding MET con imagen de fondo */}
      <Box
        sx={{
          flex: '0 0 50%',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          backgroundImage: 'url(/images/met-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(160deg, rgba(0,98,114,0.88) 0%, rgba(26,45,61,0.92) 100%)',
          }}
        />

        {/* Patrón de puntos decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            opacity: 0.06,
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Contenido */}
        <Box sx={{ textAlign: 'center', zIndex: 1, px: 6 }}>
          <img
            src="/logo-met.png"
            alt="MET Energía"
            style={{
              height: 110,
              filter: 'brightness(0) invert(1)',
              marginBottom: 28,
            }}
          />

          <Box
            sx={{
              backgroundColor: 'rgba(0, 98, 114, 0.25)',
              backdropFilter: 'blur(8px)',
              borderRadius: 2,
              p: 4,
              maxWidth: 380,
              mx: 'auto',
              mb: 4,
            }}
          >
            <Typography
              sx={{
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '1.6rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                mb: 1.5,
                lineHeight: 1.2,
              }}
            >
              MET Licitator
            </Typography>
            <Box
              sx={{
                width: 50,
                height: 3,
                backgroundColor: '#ffffff',
                mb: 2,
              }}
            />
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: '0.9rem',
                lineHeight: 1.5,
              }}
            >
              Análisis inteligente de licitaciones de energía con IA
            </Typography>
          </Box>

          {/* Badges ELECTRICIDAD / GAS */}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
            <Box
              sx={{
                px: 3.5,
                py: 1.2,
                borderRadius: '6px',
                background: COLORS.gradientElectricidad,
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
              }}
            >
              ELECTRICIDAD
            </Box>
            <Box
              sx={{
                px: 3.5,
                py: 1.2,
                borderRadius: '6px',
                background: COLORS.gradientGas,
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
              }}
            >
              GAS
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
